import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiPause, FiPlay } = FiIcons;

// ----------------------------------------------------------------------
// REFINED CONTENT STRUCTURE (4 Slides)
// ----------------------------------------------------------------------
const SLIDES = [
  {
    id: 1,
    lines: [
      "When things go wrong, as they sometimes will,",
      "When the road you're trudging seems all uphill,",
      "When the funds are low and debts are high,",
      "And you want to smile but have to sigh.",
      "When care is pressing you down a bit,",
      "Rest, if you must, but don't you quit."
    ],
    // Roots / Grounded / Uphill
    image: "https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=2560&auto=format&fit=crop",
    attribution: null
  },
  {
    id: 2,
    lines: [
      "Life is queer with its twists and turns,",
      "As everyone of us sometimes learns,",
      "And many a failure turns about,",
      "When he might have won if he'd stuck it out,",
      "Don't give up though the pace seems slow,",
      "You might succeed with another blow."
    ],
    // Twisted Branches / Complexity
    image: "https://images.unsplash.com/photo-1516214104703-d870798883c5?q=80&w=2560&auto=format&fit=crop",
    attribution: null
  },
  {
    id: 3,
    lines: [
      "Success is failure turned inside out,",
      "The silver tint of clouds of doubt,",
      "And you never can tell how close you are,",
      "It may be near when it seems afar,"
    ],
    // Forest Path / Fog / Mystery
    image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=2560&auto=format&fit=crop",
    attribution: null
  },
  {
    id: 4,
    lines: [
      "So stick to the fight when you're hardest hit,",
      "It's when things seem worst that you mustn't quit."
    ],
    // New Image: Sprawling Oak / Resilience
    image: "https://media-manager-c.questera.ai/greta-media/8210ad95efb747bb311b069cf26456dbc6f4698f846d83a8cc9125e272c4df314dbaa665e28d14a7414479ed5b72910d/images/aW1hZ2UvanBlZw==/2a4d332caf164bf787780b3ace9aa31d.jpg",
    attribution: "— Author Unknown"
  }
];

const AUTOPLAY_DURATION = 8000;

const PoeticHeroSlider = ({ height = "h-screen", overlayOpacity = 0.5 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
    }, AUTOPLAY_DURATION);
    return () => clearInterval(timer);
  }, [currentIndex, isPaused]);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % SLIDES.length);
  const prevSlide = () => setCurrentIndex((prev) => (prev === 0 ? SLIDES.length - 1 : prev - 1));

  return (
    <div 
      className={`relative w-full ${height} overflow-hidden bg-[#1a2e22] group select-none`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={currentIndex}
          className="absolute inset-0 w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2.0, ease: "easeInOut" }}
        >
          {/* Background with subtle Ken Burns */}
          <motion.div
            className="absolute inset-0 w-full h-full"
            initial={{ scale: 1.05 }}
            animate={{ scale: 1.15 }}
            transition={{ duration: 15, ease: "linear" }}
          >
            <img
              src={SLIDES[currentIndex].image}
              alt="Nature Background"
              className="w-full h-full object-cover"
            />
          </motion.div>
          
          {/* Gradient Overlays for Readability */}
          <div 
            className="absolute inset-0 bg-[#0a0f0c] mix-blend-multiply transition-opacity duration-1000"
            style={{ opacity: overlayOpacity }} 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        </motion.div>
      </AnimatePresence>

      {/* Content Container - Centered and padded to prevent cutoff */}
      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 md:p-12 z-10 pb-24">
        <div className="max-w-5xl w-full text-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 1.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center"
            >
              <div className="space-y-3 md:space-y-5 lg:space-y-6">
                {SLIDES[currentIndex].lines.map((line, idx) => (
                  <h2
                    key={idx}
                    className="serif text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl leading-tight md:leading-relaxed text-[#f3f4f6] font-light tracking-wide italic drop-shadow-md"
                  >
                    {line}
                  </h2>
                ))}
              </div>

              {/* Conditional Attribution Line */}
              {SLIDES[currentIndex].attribution && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 1 }}
                  className="mt-8 md:mt-12 flex flex-col items-center"
                >
                  <div className="w-16 h-[1px] bg-[#d4c4a8] mb-4 opacity-70 shadow-[0_0_10px_rgba(212,196,168,0.5)]" />
                  <p className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-[#d4c4a8] opacity-90 drop-shadow-sm">
                    {SLIDES[currentIndex].attribution}
                  </p>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation UI - Positioned at bottom with safe area */}
      <div className="absolute bottom-8 inset-x-0 flex flex-col items-center gap-4 z-20">
        <div className="flex items-center gap-6 md:gap-8">
          <button 
            onClick={prevSlide} 
            className="text-white/40 hover:text-white transition-colors p-2"
            aria-label="Previous Slide"
          >
            <SafeIcon icon={FiChevronLeft} className="text-2xl md:text-3xl" />
          </button>
          
          <div className="flex gap-2 md:gap-3">
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-[2px] rounded-full transition-all duration-700 ${
                  idx === currentIndex 
                    ? 'w-8 md:w-12 bg-[#d4c4a8] opacity-100' 
                    : 'w-3 md:w-4 bg-white/30 hover:bg-white/50'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
          
          <button 
            onClick={nextSlide} 
            className="text-white/40 hover:text-white transition-colors p-2"
            aria-label="Next Slide"
          >
            <SafeIcon icon={FiChevronRight} className="text-2xl md:text-3xl" />
          </button>
        </div>
        
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="text-[10px] md:text-xs uppercase tracking-widest text-white/40 hover:text-[#d4c4a8] transition-all flex items-center gap-2 py-2"
        >
          <SafeIcon icon={isPaused ? FiPlay : FiPause} />
          {isPaused ? "Resume" : "Pause"}
        </button>
      </div>
    </div>
  );
};

export default PoeticHeroSlider;